/**
 * "High Risk Vehicles" shortlist under the Vehicle-replay controls.
 *
 * Top-20 slice of the same Offender Vehicles watchlist that backs
 * `/app/offenders` (`GET /offenders/vehicles`), ranked lifetime-to-date so a
 * demo lands on the worst offenders without first having to know a
 * registration. Clicking a row hands the registration back to the panel,
 * which loads it through the SAME lookup path the search box uses.
 *
 * Fetched lazily: the whole panel only mounts while the Maps "Vehicle" tool
 * is open (`mode === 'replay'` in Maps.tsx), so the query fires on open and
 * not before.
 *
 * Failure is deliberately quiet — this list is an accelerator, never a
 * dependency. If it errors the replay controls above it keep working.
 */

import { AlertTriangle } from 'lucide-react';

import { useOffenderVehicles } from '@/api/hooks/useOffenderVehicles';
import { shortToken } from '@/lib/format';
import { SEVERITY_FILL } from '@/lib/severity';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import type { OffenderVehicleRow } from '@/types/offenders';
import type { Severity } from '@/types/alerts';

const ROW_LIMIT = 20;

/** Severity counts rendered as coloured dots, most-urgent first. */
const DOT_FIELDS: { key: keyof OffenderVehicleRow; severity: Severity }[] = [
  { key: 'critical_count', severity: 'Critical' },
  { key: 'high_count', severity: 'High' },
  { key: 'medium_count', severity: 'Medium' },
  { key: 'low_count', severity: 'Low' },
];

function SeverityDots({ row }: { row: OffenderVehicleRow }): JSX.Element | null {
  const shown = DOT_FIELDS.map(({ key, severity }) => ({
    severity,
    count: Number(row[key] ?? 0),
  })).filter((d) => d.count > 0);
  if (shown.length === 0) return null;
  return (
    <span className="flex items-center gap-1.5">
      {shown.map((d) => (
        <span
          key={d.severity}
          className="flex items-center gap-0.5 tabular-nums"
          title={`${d.count} ${d.severity}`}
        >
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: SEVERITY_FILL[d.severity] }}
          />
          {d.count}
        </span>
      ))}
    </span>
  );
}

export interface HighRiskVehiclesListProps {
  /** Registration currently loaded in the replay — gets the active highlight. */
  selectedReg?: string;
  /** Hands the registration to the panel's existing `selectVehicle` path. */
  onSelect: (registration: string) => void;
}

export default function HighRiskVehiclesList({
  selectedReg,
  onSelect,
}: HighRiskVehiclesListProps): JSX.Element {
  const { data, isLoading, isError } = useOffenderVehicles({
    period: 'LTD',
    limit: ROW_LIMIT,
  });
  const rows = data?.rows ?? [];

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          High Risk Vehicles
        </span>
        {data && (
          <span className="text-[10px] text-muted-foreground">
            top {rows.length} · {data.period_label}
          </span>
        )}
      </div>

      {isLoading && (
        <div className="flex flex-col gap-1">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      )}

      {isError && !isLoading && (
        <div className="flex items-center gap-1.5 rounded border bg-muted/40 p-2 text-[11px] text-muted-foreground">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Couldn’t load high-risk list.
        </div>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <div className="rounded border bg-muted/40 p-2 text-[11px] text-muted-foreground">
          No flagged vehicles yet.
        </div>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <div className="overflow-hidden rounded border">
          {rows.map((row) => {
            // The replay route is keyed by registration; a row whose
            // registration is still NULL upstream can't be replayed, so it
            // renders as a non-interactive reference line instead.
            const reg = row.registration;
            const active = Boolean(reg) && reg === selectedReg;
            const makeModel = [row.make, row.model].filter(Boolean).join(' ') || '—';
            return (
              <button
                key={row.asset_token}
                type="button"
                disabled={!reg}
                onClick={() => reg && onSelect(reg)}
                title={reg ? `Replay ${reg}` : 'No registration on file — cannot replay'}
                className={cn(
                  'flex w-full items-center gap-2 border-b px-2 py-1.5 text-left text-[11px] last:border-b-0',
                  reg ? 'hover:bg-accent' : 'cursor-default opacity-60',
                  active && 'bg-accent',
                )}
              >
                <span className="w-4 shrink-0 tabular-nums text-muted-foreground">
                  {row.offender_rank}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-mono font-semibold">
                    {reg ?? shortToken(row.asset_token)}
                  </span>
                  <span className="truncate text-[10px] text-muted-foreground">{makeModel}</span>
                </span>
                <span className="shrink-0 text-right text-muted-foreground">
                  <span className="block tabular-nums font-medium text-foreground">
                    {row.alert_count}
                  </span>
                  <SeverityDots row={row} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
